import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Tabs, Tab, Grid, Chip, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, Divider, CircularProgress, TextField, Rating, Alert, useTheme, alpha,
} from '@mui/material';
import {
  Visibility, RateReview, LocationOn, CalendarMonth, ConfirmationNumber, FlightTakeoff,
} from '@mui/icons-material';
import bookingsService from '../../services/bookingsService';
import reviewsService from '../../services/reviewsService';
import { getTripImageUrl } from '../../utils/tripImage';
import { formatCurrency } from '../../utils/currency';
import { PageHeader, ButtonPill, EmptyState, hoverCardSx } from '../ui';

const MyBookings = () => {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(0);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [myBookings, setMyBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewDialog, setReviewDialog] = useState(false);
  const [reviewBooking, setReviewBooking] = useState(null);
  const [reviewForm, setReviewForm] = useState({ rating: 5, title: '', comment: '' });
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [reviewError, setReviewError] = useState(null);

  useEffect(() => {
    const fetchMyBookings = async () => {
      try {
        setLoading(true);
        const response = await bookingsService.getMyBookings();
        setMyBookings(response.bookings || []);
      } catch (error) {
        console.error('Error fetching bookings:', error);
        setMyBookings([]);
      } finally {
        setLoading(false);
      }
    };

    fetchMyBookings();
  }, []);

  const date = (b) => b.travel_date || b.travelDate;
  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);
  const filterBookings = (status) => {
    if (status === 'all') return myBookings;
    if (status === 'upcoming') {
      return myBookings.filter(b => {
        const d = new Date(date(b));
        d.setHours(0, 0, 0, 0);
        return d >= todayStart && b.status !== 'cancelled';
      });
    }
    if (status === 'past') {
      // Past = the travel date has gone by, regardless of whether it was marked "completed".
      return myBookings.filter(b => {
        const d = new Date(date(b));
        d.setHours(0, 0, 0, 0);
        return d < todayStart;
      });
    }
    return myBookings;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'success';
      case 'pending': return 'warning';
      case 'completed': return 'info';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const filteredBookings = filterBookings(['all', 'upcoming', 'past'][activeTab]);

  const canLeaveReview = (b) => b.can_leave_review ?? (b.status === 'completed' && !b.has_review);

  const handleOpenReview = (booking) => {
    setReviewBooking(booking);
    setReviewForm({ rating: 5, title: '', comment: '' });
    setReviewError(null);
    setReviewDialog(true);
  };

  const handleCloseReview = () => {
    setReviewDialog(false);
    setReviewBooking(null);
    setReviewError(null);
  };

  const handleSubmitReview = async () => {
    if (!reviewBooking) return;
    setReviewSubmitting(true);
    setReviewError(null);
    try {
      await reviewsService.createReview({
        tripId: reviewBooking.trip?.id ?? reviewBooking.trip_id,
        bookingId: reviewBooking.id,
        rating: reviewForm.rating,
        title: reviewForm.title || undefined,
        comment: reviewForm.comment || undefined,
      });
      setMyBookings(prev => prev.map(b => b.id === reviewBooking.id ? { ...b, can_leave_review: false, has_review: true } : b));
      handleCloseReview();
    } catch (err) {
      setReviewError(err?.response?.data?.message ?? err?.message ?? 'Failed to submit review');
    } finally {
      setReviewSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader title="My Bookings" subtitle="Every trip you've reserved, in one place." />

      <Tabs
        value={activeTab}
        onChange={(e, v) => setActiveTab(v)}
        sx={{ mb: 3, '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 } }}
      >
        <Tab label={`All (${myBookings.length})`} />
        <Tab label={`Upcoming (${filterBookings('upcoming').length})`} />
        <Tab label={`Past (${filterBookings('past').length})`} />
      </Tabs>

      {filteredBookings.length === 0 ? (
        <EmptyState
          icon={<FlightTakeoff sx={{ fontSize: 64 }} />}
          title="Nothing here yet"
          description="When you book a trip, it will show up here."
          action={<ButtonPill variant="contained" href="/trips">Browse trips</ButtonPill>}
        />
      ) : (
        <Grid container spacing={2.5}>
          {filteredBookings.map((booking) => {
            const trip = booking.trip || {};
            const payStatus = booking.payment_status || booking.paymentStatus || '';
            return (
              <Grid item xs={12} md={6} key={booking.id}>
                <Box sx={{ ...hoverCardSx(theme), display: 'flex', flexDirection: { xs: 'column', sm: 'row' } }}>
                  <Box sx={{ position: 'relative', width: { xs: '100%', sm: 150 }, height: { xs: 150, sm: 'auto' }, flexShrink: 0, overflow: 'hidden' }}>
                    <Box component="img" src={getTripImageUrl(trip)} alt={trip.title}
                      sx={{ width: '100%', height: '100%', objectFit: 'cover', minHeight: 150 }} />
                    <Chip
                      label={booking.status}
                      color={getStatusColor(booking.status)}
                      size="small"
                      sx={{ position: 'absolute', top: 10, left: 10, fontWeight: 700, textTransform: 'capitalize' }}
                    />
                  </Box>
                  <Box sx={{ p: 2.25, flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }} noWrap>{trip.title}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }} noWrap>
                      <LocationOn sx={{ fontSize: 14 }} /> {trip.location}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 1.25, color: theme.palette.text.secondary }}>
                      <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                        <CalendarMonth sx={{ fontSize: 14 }} /> {new Date(date(booking)).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                        <ConfirmationNumber sx={{ fontSize: 14 }} /> {booking.booking_reference || booking.bookingReference}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 'auto', pt: 1.5 }}>
                      <Box>
                        <Typography sx={{ fontWeight: 800, color: theme.palette.primary.main, lineHeight: 1 }}>
                          {formatCurrency(booking.total_price ?? booking.totalPrice, booking.currency || trip.currency)}
                        </Typography>
                        <Chip label={payStatus} size="small" variant="outlined"
                          sx={{ mt: 0.5, height: 18, fontSize: '0.62rem', textTransform: 'capitalize',
                            color: payStatus.toLowerCase() === 'paid' ? theme.palette.success.main : theme.palette.warning.main,
                            borderColor: payStatus.toLowerCase() === 'paid' ? alpha(theme.palette.success.main, 0.5) : alpha(theme.palette.warning.main, 0.5) }} />
                      </Box>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {canLeaveReview(booking) && (
                          <Button size="small" startIcon={<RateReview />} sx={{ textTransform: 'none' }} onClick={() => handleOpenReview(booking)}>
                            Review
                          </Button>
                        )}
                        <ButtonPill size="small" variant="outlined" startIcon={<Visibility />}
                          onClick={() => { setSelectedBooking(booking); setOpenDialog(true); }}>
                          View
                        </ButtonPill>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      )}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Booking Details</DialogTitle>
        <DialogContent>
          {selectedBooking && (
            <Box>
              <Typography variant="h6">{selectedBooking.trip?.title}</Typography>
              <Divider sx={{ my: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={6}><Typography variant="body2" color="text.secondary">Booking Reference</Typography><Typography variant="body1">{selectedBooking.booking_reference || selectedBooking.bookingReference}</Typography></Grid>
                <Grid item xs={6}><Typography variant="body2" color="text.secondary">Status</Typography><Chip label={selectedBooking.status} color={getStatusColor(selectedBooking.status)} size="small" /></Grid>
                <Grid item xs={6}><Typography variant="body2" color="text.secondary">Travel Date</Typography><Typography variant="body1">{new Date(selectedBooking.travel_date || selectedBooking.travelDate).toLocaleDateString()}</Typography></Grid>
                <Grid item xs={6}><Typography variant="body2" color="text.secondary">Travelers</Typography><Typography variant="body1">{selectedBooking.num_travelers || selectedBooking.numTravelers}</Typography></Grid>
                <Grid item xs={12}><Typography variant="body2" color="text.secondary">Special Requests</Typography><Typography variant="body1">{selectedBooking.special_requests || selectedBooking.specialRequests || 'None'}</Typography></Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Close</Button>
          {selectedBooking && canLeaveReview(selectedBooking) && (
            <ButtonPill variant="contained" startIcon={<RateReview />} onClick={() => { setOpenDialog(false); handleOpenReview(selectedBooking); }}>
              Write Review
            </ButtonPill>
          )}
        </DialogActions>
      </Dialog>

      {/* Write Review Dialog */}
      <Dialog open={reviewDialog} onClose={handleCloseReview} maxWidth="sm" fullWidth>
        <DialogTitle>Write a Review</DialogTitle>
        <DialogContent>
          {reviewBooking && (
            <Box sx={{ pt: 1 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                How was your trip to {reviewBooking.trip?.title}?
              </Typography>
              {reviewError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setReviewError(null)}>{reviewError}</Alert>}
              <Box sx={{ my: 2 }}>
                <Typography variant="body2" gutterBottom>Rating</Typography>
                <Rating
                  value={reviewForm.rating}
                  onChange={(e, v) => setReviewForm(f => ({ ...f, rating: v ?? 5 }))}
                  size="large"
                />
              </Box>
              <TextField
                fullWidth
                label="Title (optional)"
                value={reviewForm.title}
                onChange={(e) => setReviewForm(f => ({ ...f, title: e.target.value }))}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Your review"
                multiline
                rows={4}
                value={reviewForm.comment}
                onChange={(e) => setReviewForm(f => ({ ...f, comment: e.target.value }))}
                placeholder="Share your experience..."
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseReview}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmitReview} disabled={reviewSubmitting}>
            {reviewSubmitting ? 'Submitting...' : 'Submit Review'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MyBookings;
